import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowPathIcon,
  ChevronDownIcon,
  NoSymbolIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import DashboardLayout from "../../components/DashboardLayout";
import AgentLog from "../../components/common/AgentLog";
import StatusBadge from "../../components/common/StatusBadge";
import WeaveLoader from "../../components/common/WeaveLoader";
import { setCurrentProject } from "../../redux/project/projectSlice";
import { deploymentsService } from "../../services/deployments";
import { projectsService } from "../../services/projects";
import { getApiErrorMessage } from "../../utils/axios";
import { refreshSidebarBadges } from "../../utils/sidebarBadges";
import { sitePath } from "../../utils/urls";

const ACTIVE_DEPLOY_STATUSES = new Set(["building"]);
const PAGE_SIZE = 10;

const Deploys = ({ currentUser }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const project = useSelector((state) => state.project.currentProject);
  const projectHash = project?.hash;
  const [deployments, setDeployments] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [openHash, setOpenHash] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const loadDeployments = useCallback(() => {
    if (!projectHash) return;
    deploymentsService.list(
      projectHash,
      (response) => {
        setDeployments(Array.isArray(response.data) ? response.data : response.data.results || []);
        setLoading(false);
      },
      () => {
        setError("Could not load deployments.");
        setLoading(false);
      },
    );
  }, [projectHash]);

  useEffect(() => {
    setLoading(true);
    setVisibleCount(PAGE_SIZE);
    setOpenHash("");
    setDeployments([]);
    loadDeployments();
  }, [projectHash, loadDeployments]);

  const hasActiveDeploy = deployments.some((item) => ACTIVE_DEPLOY_STATUSES.has(item.status));
  const shouldPoll = hasActiveDeploy || project?.status === "building" || project?.status === "staging";

  useEffect(() => {
    if (!projectHash || !shouldPoll) return undefined;
    const timer = setInterval(() => {
      projectsService.getProject(projectHash, (response) => {
        dispatch(setCurrentProject(response.data));
      });
      loadDeployments();
    }, 3000);
    return () => clearInterval(timer);
  }, [projectHash, shouldPoll, dispatch, loadDeployments]);

  // Auto-open the active deploy once when one appears and nothing is expanded yet.
  useEffect(() => {
    if (openHash) return;
    const building = deployments.find((item) => ACTIVE_DEPLOY_STATUSES.has(item.status));
    if (building) setOpenHash(building.hash);
  }, [deployments, openHash]);

  const deploy = () => {
    setError("");
    projectsService.deployProject(
      projectHash,
      (response) => {
        const created = response.data;
        setDeployments((prev) => [created, ...prev.filter((item) => item.hash !== created.hash)]);
        setOpenHash(created.hash);
        projectsService.getProject(projectHash, (projectResponse) => {
          dispatch(setCurrentProject(projectResponse.data));
        });
      },
      (requestError) => {
        setError(requestError?.response?.data?.error || "Could not start deploy.");
      },
    );
  };

  const retry = (deployment) => {
    setError("");
    refreshSidebarBadges();
    setOpenHash(deployment.hash);
    deploymentsService.redeploy(
      projectHash,
      deployment.hash,
      (response) => {
        const created = response.data;
        setDeployments((prev) => [created, ...prev.filter((item) => item.hash !== created.hash)]);
        setOpenHash(created.hash);
        loadDeployments();
      },
      (requestError) => {
        setError(requestError?.response?.data?.error || "Could not retry deploy.");
        loadDeployments();
        refreshSidebarBadges();
      },
    );
  };

  const fixConflicts = (deployment) => {
    setError("");
    refreshSidebarBadges();
    deploymentsService.resolveConflicts(
      projectHash,
      deployment.hash,
      () => {
        navigate(sitePath(project, "changes/"));
      },
      (requestError) => {
        setError(getApiErrorMessage(requestError, "Could not start conflict resolution."));
        refreshSidebarBadges();
      },
    );
  };

  const cancel = (deployment) => {
    setError("");
    refreshSidebarBadges();
    deploymentsService.cancel(
      projectHash,
      deployment.hash,
      () => {
        loadDeployments();
        projectsService.getProject(projectHash, (response) => {
          dispatch(setCurrentProject(response.data));
        });
      },
      (requestError) => {
        setError(requestError?.response?.data?.error || "Could not cancel deploy.");
        loadDeployments();
        refreshSidebarBadges();
      },
    );
  };

  // ponytail: button follows deployment rows only; project.status can lag after cancel/fail
  const deploying = hasActiveDeploy;
  const canDeploy = Boolean(project?.table_count) && !deploying && !project?.is_archived;
  const visible = deployments.slice(0, visibleCount);
  const hasMore = deployments.length > visibleCount;

  return (
    <DashboardLayout
      title="Deploys"
      subtitle="Production deploy history for this site."
      currentUser={currentUser}
      primaryAction={project ? (
        <button
          type="button"
          onClick={deploy}
          disabled={!canDeploy}
          className="rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-semibold text-theme-button disabled:opacity-50 lg:px-4 lg:py-2 lg:text-sm"
        >
          {deploying ? "Deploying…" : "New deploy"}
        </button>
      ) : null}
    >
      {error && <p className="mb-4 text-sm text-theme-secondary">{error}</p>}

      {loading && !deployments.length ? (
        <div className="flex items-center justify-center py-24">
          <WeaveLoader size={64} />
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((deployment) => (
            <DeploymentCard
              key={deployment.hash}
              deployment={deployment}
              project={project}
              expanded={openHash === deployment.hash}
              onToggle={() => setOpenHash(openHash === deployment.hash ? "" : deployment.hash)}
              onRetry={() => retry(deployment)}
              onFixConflicts={() => fixConflicts(deployment)}
              onCancel={() => cancel(deployment)}
            />
          ))}

          {!deployments.length && (
            <div className="rounded-xl border border-dashed border-theme-secondary bg-theme-main px-6 py-16 text-center">
              <p className="text-sm font-medium">No production deploys yet</p>
              <p className="mt-1.5 text-sm opacity-60">
                {project?.table_count
                  ? "Ship your first production deploy to see it here."
                  : "Add data before deploying your site."}
              </p>
              {canDeploy && (
                <button
                  type="button"
                  onClick={deploy}
                  className="mt-5 rounded-lg bg-theme-primary px-5 py-2 text-sm font-semibold text-theme-button"
                >
                  New deploy
                </button>
              )}
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold hover:opacity-80"
              >
                Show more
              </button>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

const DeploymentCard = ({
  deployment,
  project,
  expanded,
  onToggle,
  onRetry,
  onFixConflicts,
  onCancel,
}) => {
  const isLive = deployment.status === "live";
  const isActive = ACTIVE_DEPLOY_STATUSES.has(deployment.status);
  const durationLabel = formatDuration(deployment.create_date, deployment.resolved_at);
  const logLines = deployment.deploy_log || [];
  const canExpand = logLines.length > 0 || isActive;
  const showRetry = deployment.status === "failed" || deployment.status === "canceled";
  const showFixConflicts = deployment.status === "failed" && deployment.has_merge_conflict;

  return (
    <article
      className={`rounded-xl border bg-theme-main ${
        isLive ? "border-[1.5px] border-theme-primary" : "border-theme-secondary"
      }`}
    >
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {canExpand ? (
              <button
                type="button"
                onClick={onToggle}
                className="flex min-w-0 items-center gap-2 text-left hover:opacity-80"
              >
                <ChevronDownIcon
                  className={`h-3.5 w-3.5 shrink-0 opacity-50 transition-transform ${expanded ? "rotate-180" : ""}`}
                />
                <strong className={`text-sm ${isLive ? "" : "opacity-70"}`}>
                  v{deployment.version}
                </strong>
              </button>
            ) : (
              <strong className={`text-sm ${isLive ? "" : "opacity-70"}`}>
                v{deployment.version}
              </strong>
            )}
            <StatusBadge
              status={deployment.status}
              label={isLive ? "Live now" : undefined}
            />
            {(deployment.change_requests || []).length > 0 && (
              <Link
                to={sitePath(project, "changes/")}
                className="text-xs font-semibold text-theme-primary hover:opacity-80"
              >
                {(deployment.change_requests || []).length === 1
                  ? "View change"
                  : `View ${deployment.change_requests.length} changes`}
              </Link>
            )}
          </div>
          <p className="mt-2 text-sm opacity-70">
            {deployment.changelog || "Site deployment"}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs opacity-50">
            <time dateTime={deployment.create_date}>
              {formatDeployDate(deployment.create_date)}
            </time>
            {durationLabel && <span>{durationLabel}</span>}
            {deployment.github_sha && (
              <code className="font-mono">{deployment.github_sha.slice(0, 7)}</code>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {isActive && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-1.5 text-xs font-semibold"
            >
              <NoSymbolIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Cancel
            </button>
          )}
          {showFixConflicts && (
            <button
              type="button"
              onClick={onFixConflicts}
              className="inline-flex items-center gap-1.5 rounded-lg border border-theme-primary px-3 py-1.5 text-xs font-semibold text-theme-primary"
            >
              <WrenchScrewdriverIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Fix conflicts
            </button>
          )}
          {showRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 rounded-lg border border-theme-primary px-3 py-1.5 text-xs font-semibold text-theme-primary"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Retry
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-theme-secondary px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
          {deployment.status_message && (
            <p className="flex items-center gap-2 text-sm opacity-60">
              {isActive && (
                <span
                  className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-theme-secondary border-t-theme-primary"
                  aria-hidden="true"
                />
              )}
              {deployment.status_message}
            </p>
          )}
          {logLines.length > 0 || isActive ? (
            <AgentLog lines={logLines} live={isActive} visibleLines={6} />
          ) : (
            <p className="text-sm opacity-50">No deploy logs for this version.</p>
          )}
        </div>
      )}
    </article>
  );
};

const formatDeployDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDuration = (createDate, resolvedAt) => {
  if (!createDate || !resolvedAt) return "";
  const seconds = Math.round((new Date(resolvedAt) - new Date(createDate)) / 1000);
  if (seconds < 1) return "<1s";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

export default Deploys;
